import { Fragment, isValidElement, type ReactNode, useMemo } from "react";
import { Screen } from "styles/antTheme/Variables";
import { useMediaQuery } from "usehooks-ts";

import { BuiHeader } from "@btnet/blueprint-components";
import {
    IconArrowInSimple,
    IconArrowLeft,
    IconCatalog,
    IconClockCounterClockwise,
    IconComments,
    IconCommentsFilled,
    IconHelp,
    IconX,
} from "@btnet/blueprint-icons";
import { BTOverflow, BTTextArea } from "@btnet/core-components";
import { cn } from "@btnet/utilities-core";
import { useWebviewContext } from "@btnet/utilities-webview";

import { BdsInput, BdsLink, mappedActionIconDefaults } from "@buildertrend/components";

import { useTracking } from "utilities/analytics/analytics";
import { isObject } from "utilities/object/object";

import "commonComponents/btWrappers/BTItemHeader/BTItemHeader.less";
import { BTItemHeaderMetadata } from "commonComponents/btWrappers/BTItemHeader/BTItemHeader.Metadata";
import {
    BT_ITEM_HEADER_OVERFLOW_PADDING,
    type IBTItemHeaderActionButtonsProps,
    type IBTItemHeaderCommentButton,
    type IBTItemHeaderIconDefaults,
    type IBTItemHeaderSecondaryAction,
    type TBTItemHeaderIcon,
    type TBTItemHeaderMappedIcon,
    type TBTItemHeaderProps,
} from "commonComponents/btWrappers/BTItemHeader/BTItemHeader.types";
import "commonComponents/btWrappers/BTItemHeader/BTItemHeaderActionButtons.less";

import { BTButton, type IBTButtonPropsBase } from "../BTButton/BTButton";
import { BTMenu } from "../BTMenu/BTMenu";
import { BTPopover } from "../BTPopover/BTPopover";

const actionIconDefaults: Record<
    NonNullable<TBTItemHeaderIcon["icon"]>,
    IBTItemHeaderIconDefaults
> = {
    ...mappedActionIconDefaults,
    closeX: {
        icon: <IconX />,
        testid: "close",
        ariaLabel: "Close",
        text: "Close",
    },
    collapse: {
        icon: <IconArrowInSimple />,
        testid: "collapse",
        ariaLabel: "Collapse",
        text: "Collapse",
    },
    help: {
        icon: <IconHelp />,
        testid: "help",
        ariaLabel: "Help",
        text: "Help",
    },
    // `activity`, `history`, and `statusChange` intentionally share the clock-counter-clockwise
    // icon — they're distinct actions but use the same visual.
    activity: {
        icon: <IconClockCounterClockwise />,
        testid: "activity",
        ariaLabel: "Activity",
        text: "Activity",
    },
    history: {
        icon: <IconClockCounterClockwise />,
        testid: "history",
        ariaLabel: "History",
        text: "History",
    },
    statusChange: {
        icon: <IconClockCounterClockwise />,
        testid: "status-change",
        ariaLabel: "Change Status",
        text: "Change Status",
    },
    saveToCatalog: {
        icon: <IconCatalog />,
        testid: "saveToCatalog",
        ariaLabel: "Save to catalog",
        text: "Save to catalog",
    },
    // Shares the `copy` icon but with the "Copy to template" label some entities
    // (e.g. Job) need instead of the generic "Copy" used by list bulk actions.
    copyToTemplate: {
        icon: mappedActionIconDefaults.copy.icon,
        testid: "copy-to-template",
        ariaLabel: "Copy to template",
        text: "Copy to template",
    },
};

const BTItemHeaderCommentButton = function ({
    hasComments,
    hasUnreadComments,
    isCommentDrawerOpen,
    onCommentsClick,
    disabled,
    tooltip,
}: IBTItemHeaderCommentButton) {
    return (
        <BTPopover content={tooltip ?? "Comments"} placement="top">
            <BTButton
                type="tertiary"
                aria-label="Comment"
                data-testid="openCloseCommentDrawer"
                onClick={onCommentsClick}
                selected={isCommentDrawerOpen}
                disabled={disabled}
                className={cn("CommentsBTButton", {
                    UnreadCommentsIndicator: hasUnreadComments,
                    CommentDrawerOpen: isCommentDrawerOpen,
                })}
                icon={hasComments ? <IconCommentsFilled /> : <IconComments />}
            />
        </BTPopover>
    );
};

function getActionButtonProps(
    action: { icon: string; onClick?: () => void; target?: string; disabled?: boolean },
    iconDefaults: IBTItemHeaderIconDefaults,
    testid: string,
): IBTButtonPropsBase {
    return {
        "aria-label": iconDefaults.ariaLabel,
        type: action.icon === "delete" ? "secondarydanger" : "tertiary",
        onClick: action.onClick,
        target: action.target,
        disabled: action.disabled,
        "data-testid": testid ? `${testid}-${iconDefaults.testid}` : iconDefaults.testid,
    };
}

/**
 * The always-visible inline form of a mapped icon action: the icon button
 * (optionally wrapped in a `to` link) inside its tooltip/popover. Shared by the
 * right-aligned overflow items (`getInlineActions`) and the leading left cluster
 * (`getLeadingActions`) so the button + tooltip is built in exactly one place.
 */
function renderInlineIconButton(
    action: TBTItemHeaderMappedIcon,
    iconDefaults: IBTItemHeaderIconDefaults,
    btnProps: IBTButtonPropsBase,
) {
    const iconButton = action.to ? (
        <span>
            <BdsLink
                testid={`${btnProps["data-testid"]}-link`}
                to={action.to}
                ariaLabel={iconDefaults.ariaLabel}
            >
                <BTButton {...btnProps} icon={iconDefaults.icon} />
            </BdsLink>
        </span>
    ) : (
        <BTButton {...btnProps} icon={iconDefaults.icon} />
    );

    return (
        <BTPopover
            content={action.popoverContent ?? iconDefaults.ariaLabel}
            widthType={action.popoverWidthType}
            trigger={action.popoverContent ? "click" : "hover"}
            placement="top"
        >
            {iconButton}
        </BTPopover>
    );
}

function getMenuActions(menuActions: TBTItemHeaderIcon[] | undefined, testid: string) {
    if (!menuActions || menuActions.length === 0) {
        return null;
    }

    return menuActions?.map((action) => {
        if (action.type === "customComponent") return null;

        const iconDefaults = actionIconDefaults[action.icon];
        const btnProps = getActionButtonProps(action, iconDefaults, testid);
        const iconButton = action.to ? (
            <BdsLink
                testid={`${btnProps["data-testid"]}-link`}
                to={action.to}
                ariaLabel={iconDefaults.ariaLabel}
            >
                <BTButton {...btnProps}>{iconDefaults.text}</BTButton>
            </BdsLink>
        ) : (
            <BTButton {...btnProps}>{iconDefaults.text}</BTButton>
        );

        return (
            <BTMenu.Item
                id={action.icon}
                key={action.icon}
                testid={
                    testid
                        ? `${testid}-${iconDefaults.testid}-menu-item`
                        : `${iconDefaults.testid}-menu-item`
                }
            >
                {iconButton}
            </BTMenu.Item>
        );
    });
}

function getInlineActions(inlineActions: TBTItemHeaderIcon[] | undefined, testid: string) {
    if (!inlineActions || inlineActions.length === 0) {
        return null;
    }

    return inlineActions?.map((action) => {
        if (action.type === "customComponent") {
            // Comments are pinned globally: a CommentButton never rolls into the
            // overflow menu, regardless of whether the consumer set `pinned`. Detected
            // by element type so it holds no matter what `id` the consumer gives it.
            const isCommentButton =
                isValidElement(action.customComponent) &&
                action.customComponent.type === BTItemHeaderCommentButton;
            return (
                <BTOverflow.Item
                    id={action.id}
                    key={action.id}
                    pinned={action.pinned || isCommentButton}
                >
                    {action.customComponent}
                </BTOverflow.Item>
            );
        }

        const iconDefaults = actionIconDefaults[action.icon];
        const btnProps = getActionButtonProps(action, iconDefaults, testid);

        // The rolled-up (overflow menu) version must carry the same behavior the inline
        // button gets from its wrappers — the `to` link and the `popoverContent` popover —
        // otherwise an action whose behavior lives entirely in those wrappers (e.g.
        // Activity/Share, whose `onClick` is a no-op) goes dead once it rolls up. A bare
        // `onClick` action (Delete/Print) needs no wrapper; its handler rides on `btnProps`.
        let overflowContent = <BTButton {...btnProps}>{iconDefaults.text}</BTButton>;
        if (action.to) {
            overflowContent = (
                <BdsLink
                    testid={`${btnProps["data-testid"]}-link`}
                    to={action.to}
                    ariaLabel={iconDefaults.ariaLabel}
                >
                    {overflowContent}
                </BdsLink>
            );
        }
        if (action.popoverContent) {
            overflowContent = (
                <BTPopover
                    content={action.popoverContent}
                    widthType={action.popoverWidthType}
                    trigger="click"
                    placement="top"
                    // Keep the click from bubbling to the overflow menu's dropdown, which
                    // otherwise closes on item-click and would unmount the popover the
                    // instant it opened.
                    stopPropagation
                >
                    {overflowContent}
                </BTPopover>
            );
        }

        return (
            <BTOverflow.Item
                id={action.icon}
                key={action.icon}
                pinned={action.pinned}
                overflowContent={overflowContent}
            >
                {renderInlineIconButton(action, iconDefaults, btnProps)}
            </BTOverflow.Item>
        );
    });
}

/**
 * Distinguishes the `{ node, pinned }` secondary-action config from a raw
 * `ReactNode`. A React element exposes no top-level `node` property, so its
 * presence reliably marks the config object (and primitives/null are excluded
 * by the `object` guard).
 */
const isSecondaryActionConfig = (
    action: ReactNode | IBTItemHeaderSecondaryAction,
): action is IBTItemHeaderSecondaryAction => isObject(action) && "node" in action;

/**
 * Resolves a secondary action (raw `ReactNode` or `{ node, pinned }` config) to the
 * `node`/`pinned` pair the overflow item needs. Raw nodes are never pinned.
 */
const normalizeSecondaryAction = (
    action: ReactNode | IBTItemHeaderSecondaryAction,
): { node: ReactNode; pinned: boolean } =>
    isSecondaryActionConfig(action)
        ? { node: action.node, pinned: action.pinned ?? false }
        : { node: action, pinned: false };

/**
 * Renders `leading` icon actions as a bare left-aligned cluster — the same
 * inline button + tooltip the right-side actions use (`renderInlineIconButton`),
 * but never wrapped in an overflow item, so they always stay visible. Used for
 * panel window chrome (close/expand). Callers guard on a non-empty list.
 */
function getLeadingActions(leadingActions: TBTItemHeaderIcon[], testid: string) {
    return leadingActions.map((action) => {
        if (action.type === "customComponent") {
            return <Fragment key={action.id}>{action.customComponent}</Fragment>;
        }

        const iconDefaults = actionIconDefaults[action.icon];
        const btnProps = getActionButtonProps(action, iconDefaults, testid);

        return (
            <Fragment key={action.icon}>
                {renderInlineIconButton(action, iconDefaults, btnProps)}
            </Fragment>
        );
    });
}

const BTItemHeaderActionButtons = ({
    backLink,
    backLinkOnClick,
    closeOnClick,
    className,
    iconActions,
    primaryAction,
    secondaryActions,
    testid,
}: IBTItemHeaderActionButtonsProps) => {
    const webviewContext = useWebviewContext();
    const isMdBreakpoint = useMediaQuery(`(max-width: ${Screen.md}px)`);
    const isWebview = webviewContext !== undefined;

    // The left slot holds a single navigation affordance. A close (X) takes the
    // place of the back arrow when provided (panel/modal dismiss), matching the
    // design guide — the two are mutually exclusive. Neither renders in webview,
    // which supplies its own navigation chrome.
    let headerLeftNav: ReactNode = null;
    if (!isWebview && closeOnClick) {
        headerLeftNav = (
            <BdsLink ariaLabel="Close" onClick={closeOnClick} testid={`${testid}-close`}>
                <IconX />
            </BdsLink>
        );
    } else if (!isWebview && (backLink || backLinkOnClick)) {
        headerLeftNav = (
            <BdsLink
                ariaLabel="Back Link"
                onClick={backLinkOnClick}
                testid={`${testid}-back-link`}
                to={backLink}
            >
                <IconArrowLeft />
            </BdsLink>
        );
    }

    // These are the actions that will be visible either as inline buttons or in the overflow menu. Hidden actions will not be rendered at all.
    const visibleActions = useMemo(
        () => iconActions?.filter((action) => !action.isHidden) ?? [],
        [iconActions],
    );

    // `leading` actions form a far-left cluster (always visible); everything else
    // shares the right-aligned overflow with the primary/secondary actions.
    const leadingActions = useMemo(
        () => visibleActions.filter((action) => action.leading),
        [visibleActions],
    );
    const rightActions = useMemo(
        () => visibleActions.filter((action) => !action.leading),
        [visibleActions],
    );

    // Separate the right-side actions into those that should be rendered as inline buttons and those that should only be rendered in the overflow menu based on the `inMenu` property or `inMenuMdBreakpoint` property.
    const menuActions = useMemo(() => {
        const mdBreakpointActions = rightActions.filter(
            (action) => isMdBreakpoint && action.inMenuMdBreakpoint,
        );
        const inMenuActions = rightActions.filter((action) => action.inMenu);
        return [...mdBreakpointActions, ...inMenuActions];
    }, [rightActions, isMdBreakpoint]);

    // Actions that are not hidden and not marked as inMenu will be rendered as inline buttons.
    const inlineActions = useMemo(
        () =>
            rightActions.filter(
                (action) => !action.inMenu && !(isMdBreakpoint && action.inMenuMdBreakpoint),
            ),
        [rightActions, isMdBreakpoint],
    );

    const secondaryActionIds = useMemo(
        () => secondaryActions?.map((_, index) => `secondary-action-${index}-overflow-item`) ?? [],
        [secondaryActions],
    );

    const overflowIds = useMemo(() => {
        const inlineIds = inlineActions.map((action) =>
            action.type === "customComponent" ? action.id : action.icon,
        );
        return [...inlineIds, ...secondaryActionIds];
    }, [inlineActions, secondaryActionIds]);

    // Reserve overflow padding proportional to the number of (right-side) visible
    // actions so the Fluent overflow manager (which doesn't measure inter-item
    // spacing) rolls icons up at the right point instead of under-counting width
    // and overflowing the row. Leading actions sit outside the overflow.
    const visibleActionCount =
        rightActions.length + (secondaryActions?.length ?? 0) + (primaryAction ? 1 : 0);
    const padding = BT_ITEM_HEADER_OVERFLOW_PADDING * visibleActionCount;

    return (
        <div className={cn("bt-item-header-action-buttons", className)}>
            {headerLeftNav}
            {leadingActions.length > 0 && (
                <div className="flex items-center gap-2 shrink-0">
                    {getLeadingActions(leadingActions, testid)}
                </div>
            )}
            <BTOverflow
                // With a leading cluster present, grow to fill the row so the
                // overflow's content still right-aligns opposite the left cluster.
                className={cn("justify-end", leadingActions.length > 0 && "grow min-w-0")}
                testid={`${testid}-overflow`}
                overflowDirection="start"
                padding={
                    // Reserve two action-widths for the left nav affordance (back or
                    // close), which sits outside the overflow.
                    padding + (headerLeftNav ? BT_ITEM_HEADER_OVERFLOW_PADDING * 2 : 0)
                }
            >
                <BTOverflow.Menu testid={`${testid}-overflow-menu`} ids={overflowIds}>
                    {getMenuActions(menuActions, testid)}
                </BTOverflow.Menu>
                {getInlineActions(inlineActions, testid)}
                {/* Secondary actions roll up by default; only those passed as a
                    { node, pinned: true } config stay pinned (e.g. Cancel). */}
                {secondaryActions?.map((action, index) => {
                    const { node, pinned } = normalizeSecondaryAction(action);
                    return (
                        <BTOverflow.Item
                            id={secondaryActionIds[index]}
                            key={secondaryActionIds[index]}
                            pinned={pinned}
                        >
                            {node}
                        </BTOverflow.Item>
                    );
                })}
                {primaryAction && (
                    <BTOverflow.Item id="primary-action-overflow-item" pinned>
                        {primaryAction}
                    </BTOverflow.Item>
                )}
            </BTOverflow>
        </div>
    );
};

const BTItemHeaderComponent = function ({
    ariaLabel,
    children,
    className,
    testid,
}: TBTItemHeaderProps) {
    const { Track } = useTracking({ component: "BTItemHeader", uniqueId: testid });

    return (
        <Track>
            <BuiHeader
                ariaLabel={ariaLabel}
                className={cn("bt-item-header", className)}
                testid={testid}
            >
                {children}
            </BuiHeader>
        </Track>
    );
};

/**
 * BTItemHeader is the default component to display a header with/without children.
 * @param props - IBTItemHeaderProps defining the properties of the component.
 * @param props.ariaLabel - The aria-label for the header for accessibility.
 * @param props.children - Optional children elements to be rendered within the header.
 * @param props.className - Optional additional class name(s) for custom styling of the header.
 * @param props.testid - The test ID for the header component.
 *
 * @returns JSX.Element - The rendered BTItemHeader component.
 */
export const BTItemHeader = Object.assign(BTItemHeaderComponent, {
    /**
     * BTItemHeaderActionButtons is a sub-component of BTItemHeader that renders action buttons, including a back link, inline action buttons, and secondary actions within an overflow menu. It handles responsive behavior for action visibility based on screen size.
     * @param props - IBTItemHeaderActionButtonsProps defining the properties of the action buttons.
     * @param props.backLink - Optional URL for the back link. If provided, a back link will be rendered.
     * @param props.backLinkOnClick - Optional click handler for the back link. If provided, it will be called when the back link is clicked.
     * @param props.closeOnClick - Optional click handler for a left-anchored close (X). When provided, the X replaces the back arrow in the left slot (panel/modal dismiss); the two are mutually exclusive.
     * @param props.iconActions - Optional array of action objects defining the icon actions to be rendered as buttons or in the overflow menu. Each action can specify properties such as `icon`, `onClick`, `to`, `inMenu`, and `inMenuMdBreakpoint`.
     * @param props.primaryAction - Optional primary action button to be pinned in the overflow menu.
     * @param props.secondaryActions - Optional array of secondary action elements to be rendered in the overflow menu.
     * @param props.testid - The test ID for the action buttons component, used for testing purposes.
     *
     * @returns JSX.Element - The rendered BTItemHeaderActionButtons component.
     *
     * Note: The back link will not be rendered if the component is used within a webview context, as webviews typically provide their own navigation controls.
     *
     * @example
     * <BTItemHeader>
     *   <BTItemHeader.ActionButtons
     *     backLink="/previous-page"
     *     backLinkOnClick={() => console.log("Back link clicked")}
     *     iconActions={[
     *       { icon: "edit", onClick: () => console.log("Edit action") },
     *       { icon: "delete", onClick: () => console.log("Delete action"), inMenu: true },
     *     ]}
     *     primaryAction={<BTButton>Primary</BTButton>}
     *     secondaryActions={[<BTButton>Secondary 1</BTButton>, <BTButton>Secondary 2</BTButton>]}
     *   />
     * </BTItemHeader>
     */
    ActionButtons: BTItemHeaderActionButtons,
    /**
     * BTItemHeaderCommentButton is a sub-component of BTItemHeader that renders a comment toggle button.
     * It displays a filled or outlined icon depending on whether the entity has comments, shows an unread
     * indicator, and reflects the selected state while the comment drawer is open.
     * @param props - IBTItemHeaderCommentButton defining the properties of the comment button.
     * @param props.hasComments - Whether the entity has any comments. Controls the filled vs outlined
     *                            icon state.
     * @param props.hasUnreadComments - Whether there are unread comments. When true, a visual indicator
     *                                  is shown on the comment icon.
     * @param props.isCommentDrawerOpen - Whether the comment drawer is currently open. Controls the
     *                                    selected/active state of the button.
     * @param props.onCommentsClick - Callback invoked when the comment button is clicked.
     *                                Typically toggles the comment drawer.
     *
     * @returns JSX.Element - The rendered BTItemHeaderCommentButton component.
     *
     * @example
     * <BTItemHeader>
     *   <BTItemHeader.CommentButton
     *     hasComments={comments.length > 0}
     *     hasUnreadComments={unreadCount > 0}
     *     isCommentDrawerOpen={isDrawerOpen}
     *     onCommentsClick={() => setIsDrawerOpen((open) => !open)}
     *   />
     * </BTItemHeader>
     */
    CommentButton: BTItemHeaderCommentButton,
    /**
     * BTItemHeader.Metadata is a sub-component that combines a status badge with metadata text.
     * It's designed to be used within BTItemHeader to display consistent status and metadata information.
     * @param props - IBTItemHeaderMetadataProps defining the properties of the metadata component.
     * @param props.badgeText - The status badge text (e.g., "Draft", "Sent")
     * @param props.badgeDisplayType - The display type for the badge (e.g., "default", "warning")
     * @param props.metadataText - The metadata text to display (e.g., "by John Greene at 12:30 PM, Nov 6, 2026")
     * @param props.testid - Optional test ID
     * @param props.className - Optional CSS class name
     *
     * @returns JSX.Element - The rendered metadata component.
     *
     * @example
     * <BTItemHeader.Metadata
     *   badgeText="Draft"
     *   badgeDisplayType="default"
     *   metadataText="by John Greene at 12:30 PM, Nov 6, 2026"
     *   testid="item-metadata"
     * />
     */
    Metadata: BTItemHeaderMetadata,
    Input: BdsInput,
    TextArea: BTTextArea,
});
